import type { CreditCard } from "@b2bportal/card-api";
import {
  type ProductBookingTimeWithinRange,
  ProductCriteriaEnum,
  type ProductWithDestinationCountries,
  type Promotion,
  CriteriaEnum,
  type PayWithCardBin,
  CardCriteriaEnum,
} from "@b2bportal/hsbc-promotions-api";
import { Product } from "@b2bportal/purchase-api";
import { isDefined } from "@hopper-b2b/utilities";
import type { HsbcPromotedDestinations } from "./types";

type GetActivePremierDestinationsParams = {
  promotion?: Promotion;
  promotedDestinations: Array<HsbcPromotedDestinations>;
};

export const isWithinTimeRange = (promotion: Promotion): boolean => {
  const now = new Date();
  const timeRangeCriteria = promotion.applicability.criterias.find(
    (criteria) =>
      criteria.Criteria === CriteriaEnum.ProductCriteria &&
      criteria.ProductCriteria ===
        ProductCriteriaEnum.ProductBookingTimeWithinRange
  ) as ProductBookingTimeWithinRange | undefined;
  if (!timeRangeCriteria) return true;
  const start = new Date(timeRangeCriteria.start);
  const end = new Date(timeRangeCriteria.end);
  return now >= start && now <= end;
};

export const isDestinationCountryApplicable = (
  promotion: Promotion,
  country?: string
): boolean => {
  if (!country) return true;
  const countryCriteria = promotion.applicability.criterias.find(
    (criteria) =>
      criteria.Criteria === CriteriaEnum.ProductCriteria &&
      criteria.ProductCriteria ===
        ProductCriteriaEnum.ProductWithDestinationCountries
  ) as ProductWithDestinationCountries | undefined;
  if (!countryCriteria) return true;
  return countryCriteria.countryCodes.includes(country);
};

export const isProductApplicable = (
  promotion: Promotion,
  product?: Product
): boolean => {
  if (!product) return true;
  const criteriaMap = {
    [ProductCriteriaEnum.IsAir]: Product.Flight,
    [ProductCriteriaEnum.IsLodging]: Product.Hotel,
    [ProductCriteriaEnum.IsGround]: Product.Ground,
  };

  const validCriteria = Object.keys(criteriaMap);

  const productCriteria = promotion.applicability.criterias.find(
    (criteria) =>
      criteria.Criteria === CriteriaEnum.ProductCriteria &&
      validCriteria.includes(criteria.ProductCriteria) &&
      criteriaMap[criteria.ProductCriteria] != null
  );

  return (
    productCriteria == null ||
    (productCriteria.Criteria === CriteriaEnum.ProductCriteria &&
      criteriaMap[productCriteria.ProductCriteria] === product)
  );
};

export const isCardApplicable = (
  promotion: Promotion,
  cards?: CreditCard[]
): boolean => {
  if (!cards || cards.length === 0) return true;
  const binCriteria = promotion.applicability.criterias.find(
    (criteria) =>
      criteria.Criteria === CriteriaEnum.CardCriteria &&
      criteria.CardCriteria === CardCriteriaEnum.PayWithCardBin
  ) as PayWithCardBin | undefined;

  return (
    binCriteria == null ||
    cards.find((card) => binCriteria.bin.includes(card.bin)) != null
  );
};

export const isPromotionApplicable = (
  promotion: Promotion,
  context: {
    product?: Product;
    country?: string;
    cards?: CreditCard[];
  } = {}
): boolean => {
  const { product, country, cards } = context;
  return (
    isProductApplicable(promotion, product) &&
    isDestinationCountryApplicable(promotion, country) &&
    isCardApplicable(promotion, cards) &&
    isWithinTimeRange(promotion)
  );
};

const getActiveCountryCodes = (promotion: Promotion) =>
  promotion.applicability.criterias.find(
    (criteria) =>
      criteria.Criteria === CriteriaEnum.ProductCriteria &&
      criteria.ProductCriteria ===
        ProductCriteriaEnum.ProductWithDestinationCountries
  ) as ProductWithDestinationCountries | undefined;

export const getActivePremierDestinations = ({
  promotion,
  promotedDestinations,
}: GetActivePremierDestinationsParams) => {
  if (!promotion) return [];

  const countryCriteria = getActiveCountryCodes(promotion);
  if (!countryCriteria) return [];

  return countryCriteria.countryCodes
    .map((code) =>
      promotedDestinations.find(({ countryCode }) => countryCode === code)
    )
    .filter(isDefined);
};
